import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Check, Loader2 } from 'lucide-react';

interface LeadFormModalProps {
  open: boolean;
  onClose: () => void;
  preselectedPackage?: 'foundation' | 'professional' | 'enterprise' | 'not_sure';
  sourcePage?: string;
}

interface LeadFormData {
  organizationName: string;
  contactName: string;
  email: string;
  phone: string;
  campSize: string;
  interestedIn: 'foundation' | 'professional' | 'enterprise' | 'not_sure';
  timeline: string;
  message: string;
  heardAbout: string;
  website: string; // Honeypot field
}

export const LeadFormModal = ({ 
  open, 
  onClose, 
  preselectedPackage = 'not_sure',
  sourcePage = 'unknown'
}: LeadFormModalProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  const [formData, setFormData] = useState<LeadFormData>({
    organizationName: '',
    contactName: '',
    email: '',
    phone: '',
    campSize: '',
    interestedIn: preselectedPackage,
    timeline: '',
    message: '',
    heardAbout: '',
    website: '' // Honeypot
  });

  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {};

    if (!formData.organizationName || formData.organizationName.length < 2) {
      newErrors.organizationName = 'Organization name must be at least 2 characters';
    }

    if (!formData.contactName || formData.contactName.length < 2) {
      newErrors.contactName = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.campSize && (isNaN(Number(formData.campSize)) || Number(formData.campSize) < 1)) {
      newErrors.campSize = 'Please enter a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const captureUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (formData.website) {
      toast({
        title: "Error",
        description: "Invalid submission detected",
        variant: "destructive"
      });
      return;
    }

    // Rate limiting (client-side)
    const now = Date.now();
    if (now - lastSubmitTime < 60000) {
      toast({
        title: "Please wait",
        description: "You can only submit one request per minute",
        variant: "destructive"
      });
      return;
    }

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please check the form and try again",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const utmParams = captureUTMParams();

      // Call the submit_lead RPC function
      const { data, error } = await supabase.rpc('submit_lead', {
        p_organization_name: formData.organizationName,
        p_contact_name: formData.contactName,
        p_email: formData.email,
        p_phone: formData.phone || null,
        p_camp_size: formData.campSize ? parseInt(formData.campSize) : null,
        p_interested_in: formData.interestedIn,
        p_message: formData.message || null,
        p_source_page: sourcePage,
        p_timeline: formData.timeline || null,
        p_heard_about: formData.heardAbout || null,
        p_utm_source: utmParams.utm_source,
        p_utm_medium: utmParams.utm_medium,
        p_utm_campaign: utmParams.utm_campaign
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; lead_id?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit lead');
      }

      // Trigger email notification edge function
      const { error: emailError } = await supabase.functions.invoke('send-lead-notification', {
        body: { leadId: result.lead_id }
      });

      if (emailError) {
        console.error('Email notification error:', emailError);
        // Don't fail the submission if email fails
      }

      setLastSubmitTime(now);
      setSuccess(true);

      // Track analytics event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'lead_form_submitted', {
          package: formData.interestedIn,
          source_page: sourcePage
        });
      }

    } catch (error) {
      console.error('Lead submission error:', error);
      toast({
        title: "Submission Failed",
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive"
      });

      // Track analytics event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'lead_form_failed', {
          error: error instanceof Error ? error.message : 'unknown'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSuccess(false);
      setFormData({
        organizationName: '',
        contactName: '',
        email: '',
        phone: '',
        campSize: '',
        interestedIn: 'not_sure',
        timeline: '',
        message: '',
        heardAbout: '',
        website: ''
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] bg-ivory/95 backdrop-blur-md border-copper/20">
        <DialogHeader>
          <DialogTitle className="text-2xl font-playfair text-midnight-green">
            {success ? "Thank You!" : "Request Your Custom Quote"}
          </DialogTitle>
          <DialogDescription className="text-deep-teal">
            {success 
              ? "We'll be in touch within 1 business day" 
              : "Tell us about your camp and we'll send a tailored proposal"
            }
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center p-8 animate-fade-in">
            <div className="mb-4">
              <Check className="h-16 w-16 text-green-500 mx-auto animate-scale-in" />
            </div>
            <h3 className="text-2xl font-playfair font-bold text-midnight-green mb-2">
              Thanks—your request is in!
            </h3>
            <p className="text-gray-600 mb-6">
              We'll reach out within 1 business day with a tailored quote and demo link so you can see the system live.
            </p>
            <Button onClick={handleClose} variant="outline" className="border-copper text-copper hover:bg-copper/10">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot field - hidden from users */}
            <div className="hidden">
              <Input
                type="text"
                name="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="organizationName">Organization Name *</Label>
                <Input
                  id="organizationName"
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  className={errors.organizationName ? 'border-red-500' : ''}
                  required
                />
                {errors.organizationName && (
                  <p className="text-sm text-red-500 mt-1">{errors.organizationName}</p>
                )}
              </div>

              <div>
                <Label htmlFor="contactName">Contact Name *</Label>
                <Input
                  id="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className={errors.contactName ? 'border-red-500' : ''}
                  required
                />
                {errors.contactName && (
                  <p className="text-sm text-red-500 mt-1">{errors.contactName}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={errors.email ? 'border-red-500' : ''}
                  required
                />
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="campSize">Number of Campers</Label>
                <Input
                  id="campSize"
                  type="number"
                  min="1"
                  value={formData.campSize}
                  onChange={(e) => setFormData({ ...formData, campSize: e.target.value })}
                  className={errors.campSize ? 'border-red-500' : ''}
                />
                {errors.campSize && (
                  <p className="text-sm text-red-500 mt-1">{errors.campSize}</p>
                )}
              </div>

              <div>
                <Label htmlFor="interestedIn">Package Interest</Label>
                <Select 
                  value={formData.interestedIn} 
                  onValueChange={(value: any) => setFormData({ ...formData, interestedIn: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="foundation">Foundation</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="not_sure">Not Sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="timeline">Desired Start Date / Season</Label>
              <Input
                id="timeline"
                placeholder="e.g., Summer 2025, Pre-season planning"
                value={formData.timeline}
                onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="message">Current Challenges (optional)</Label>
              <Textarea
                id="message"
                placeholder="Tell us about your current meal planning process and any pain points..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="heardAbout">How did you hear about us?</Label>
              <Input
                id="heardAbout"
                placeholder="e.g., Google search, referral, conference"
                value={formData.heardAbout}
                onChange={(e) => setFormData({ ...formData, heardAbout: e.target.value })}
              />
            </div>

            <div className="text-xs text-gray-500 pt-2">
              By submitting you agree to our Privacy Policy
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-copper hover:bg-copper/90 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
